import { tint, shade, transparentize } from 'polished'
import base from './base'

const BLUE: App.PrimitiveColourRange = {
  50: '#FFFFFF',
  100: '#D6DBEE',
  200: '#ACB8DD',
  300: '#8394CD',
  400: '#5971BC',
  500: '#304DAB',
  600: '#263E89',
  700: '#1D2E67',
  800: '#131F44',
  900: '#0A0F22',
}

const LIGHT_BLUE: App.PrimitiveColourRange = {
  50: '#E0F1FC',
  100: '#C2E2F8',
  200: '#A3D4F5',
  300: '#A3D4F5',
  400: '#66B7EE',
  500: '#47A9EB',
  600: '#3987BC',
  700: '#2B658D',
  800: '#1C445E',
  900: '#0E222F',
}

const ULTRALUX_COLOUR = '#A9A187'

export const luxuryEscapesDefaultPalette: App.Palette = {
  primary: {
    normal: '#363A45',
    darkest: shade(0.24, '#363A45'),
    dark: shade(0.16, '#363A45'),
    light: tint(0.76, '#363A45'),
    lightest: tint(0.86, '#363A45'),
    hover: tint(0.16, '#363A45'),
    active: tint(0.24, '#363A45'),
  },
  neutral: {
    one: '#363A45',
    one20: transparentize(0.2, '#363A45'),
    two: '#4A4E58',
    three: '#7E8188',
    four: '#AFB0B5',
    five: '#DFDFE1',
    five40: transparentize(0.4, '#DFDFE1'),
    six: '#EFEFF0',
    six25: transparentize(0.25, '#EFEFF0'),
    seven: '#F7F7F8',
    eight: '#FFFFFF',
    clear: transparentize(0.84, '#FFFFFF'),
  },
  product: {
    exclusive: '#A3875C',
    lastMinute: '#DA3146',
    partner: '#7E8188',
    premium: '#4A4E58',
    ultralux: {
      normal: ULTRALUX_COLOUR,
      active: shade(0.24, ULTRALUX_COLOUR),
      hover: shade(0.16, ULTRALUX_COLOUR),
    },
    loyalty: {
      normal: BLUE[500],
      active: shade(0.24, BLUE[500]),
      hover: shade(0.16, BLUE[500]),
    },
  },
  control: {
    black100: '#000000',
    black25: transparentize(0.25, '#000000'),
    white16: transparentize(0.16, '#FFFFFF'),
    white24: transparentize(0.24, '#FFFFFF'),
  },
  messaging: {
    success: {
      main: '#3BAB53',
      light: '#B7F7C5',
      dark: '#2D823F',
    },
    warning: {
      main: '#E49F4E',
      light: '#FDF1E3',
      dark: '#BA8240',
    },
    urgency: {
      main: '#DA3146',
      light: '#F98190',
      dark: '#DA3146',
    },
    fast: {
      main: '#6B6ADA',
      light: tint(0.9, '#6B6ADA'),
      dark: shade(0.16, '#6B6ADA'),
    },
    critical: {
      main: '#EF4E6B',
      light: '#F9B8C4',
      dark: '#C74159',
    },
    favourite: {
      main: '#EF4E6B',
      light: '#F9B8C4',
      dark: '#C74159',
    },
    loyalty: {
      main: LIGHT_BLUE[500],
      light: LIGHT_BLUE[50],
      dark: LIGHT_BLUE[600],
    },
  },
  highlight: {
    primary: {
      normal: '#00F1BD',
      light: '#E6FEF9',
    },
    secondary: {
      normal: '#01B18B',
      light: '#E6F7F3',
      active: shade(0.24, '#01B18B'),
    },
    tertiary: {
      normal: '#6B6ADA',
      light: '#F0F0Fb',
    },
    loyalty: {
      normal: LIGHT_BLUE[500],
      light: LIGHT_BLUE[50],
    },
  },
}

export const luxuryEscapesInversePalette: App.Palette = {
  primary: {
    normal: '#FFFFFF',
    darkest: shade(0.24, '#FFFFFF'),
    dark: shade(0.16, '#FFFFFF'),
    light: tint(0.76, '#FFFFFF'),
    lightest: tint(0.86, '#FFFFFF'),
    hover: tint(0.16, '#FFFFFF'),
    active: tint(0.24, '#FFFFFF'),
  },
  neutral: {
    one: '#FFFFFF',
    one20: transparentize(0.2, '#FFFFFF'),
    two: '#EBEBEC',
    three: '#B7B8BC',
    four: '#86898F',
    five: '#767980',
    five40: transparentize(0.4, '#767980'),
    six: '#565A63',
    six25: transparentize(0.25, '#565A63'),
    seven: '#3E424C',
    eight: '#363A45',
    clear: transparentize(0.84, '#FFFFFF'),
  },
  product: {
    exclusive: '#DFB678',
    lastMinute: '#DA3146',
    partner: '#B7B8BC',
    premium: '#EBEBEC',
    ultralux: {
      normal: ULTRALUX_COLOUR,
      active: shade(0.24, ULTRALUX_COLOUR),
      hover: shade(0.16, ULTRALUX_COLOUR),
    },
    loyalty: {
      normal: BLUE[50],
      active: shade(0.24, BLUE[50]),
      hover: shade(0.16, BLUE[50]),
    },
  },
  control: {
    black100: '#000000',
    black25: transparentize(0.25, '#000000'),
    white16: transparentize(0.16, '#FFFFFF'),
    white24: transparentize(0.24, '#FFFFFF'),
  },
  messaging: {
    success: {
      main: '#3BAB53',
      light: tint(0.9, '#3BAB53'),
      dark: shade(0.16, '#3BAB53'),
    },
    warning: {
      main: '#E49F4E',
      light: tint(0.9, '#E49F4E'),
      dark: shade(0.16, '#E49F4E'),
    },
    urgency: {
      main: '#DA3146',
      light: tint(0.9, '#DA3146'),
      dark: shade(0.16, '#DA3146'),
    },
    fast: {
      main: '#6B6ADA',
      light: tint(0.9, '#6B6ADA'),
      dark: shade(0.16, '#6B6ADA'),
    },
    critical: {
      main: '#EF4E6B',
      light: tint(0.9, '#EF4E6B'),
      dark: shade(0.16, '#EF4E6B'),
    },
    favourite: {
      main: '#EF4E6B',
      light: tint(0.9, '#EF4E6B'),
      dark: shade(0.16, '#EF4E6B'),
    },
    loyalty: {
      main: LIGHT_BLUE[500],
      light: LIGHT_BLUE[50],
      dark: LIGHT_BLUE[200],
    },
  },
  highlight: {
    primary: {
      normal: '#00F1BD',
      light: '#E6FEF9',
    },
    secondary: {
      normal: '#01B18B',
      light: '#E6F7F3',
      active: shade(0.24, '#01B18B'),
    },
    tertiary: {
      normal: '#6B6ADA',
      light: '#F0F0Fb',
    },
    loyalty: {
      normal: LIGHT_BLUE[500],
      light: LIGHT_BLUE[50],
    },
  },
}

const luxuryEscapesTheme: App.Theme = {
  ...base,
  typography: {
    heading: {
      one: {
        weight: 'semiBold',
      },
      oneAlt: {
        letterSpacing: '-0.5px',
        weight: 'semiBold',
      },
      two: {
        weight: 'semiBold',
      },
      twoAlt: {
        weight: 'semiBold',
      },
      three: {
        weight: 'semiBold',
      },
      threeAlt: {
        weight: 'semiBold',
        letterSpacing: '-0.5px',
      },
      four: {
        weight: 'semiBold',
      },
      five: {
        weight: 'superBold',
      },
      six: {
        weight: 'superBold',
      },
    },
  },
  borderRadius: {
    XS: '0px',
    S: '2px',
    M: '4px',
    L: '8px',
    round: '9999px',
  },
  font: {
    primary: {
      provider: 'local',
      localFileSrc: '/fonts/LE-fonts.css',
      name: 'Suisse Int\'l\'',
      family: '"Suisse Int\'l", "Helvetica Neue", Helvetica, Arial, sans-serif',
      weight: {
        regular: '400',
        bold: '500',
        semiBold: '500',
        superBold: '600',
      },
    },
    secondary: {
      provider: 'local',
      localFileSrc: '/fonts/LE-fonts.css',
      name: '"Suisse Int\'l\'"',
      family: '"Suisse Int\'l\", "Helvetica Neue", Helvetica, Arial, sans-serif',
      weight: {
        superBold: '600',
        bold: '500',
        semiBold: '450',
        regular: '400',
      },
    },
    secondaryAlternate: {
      provider: 'local',
      localFileSrc: '/fonts/LE-fonts.css',
      name: 'Suisse Works',
      family: '"Suisse Works", "Suisse Int\'l", "Helvetica Neue", Helvetica, Arial, sans-serif',
      weight: {
        // only semi-bold is offered by this alternate font
        bold: '450',
        semiBold: '450',
        regular: '450',
        superBold: '450',
      },
    },
  },
}

export default luxuryEscapesTheme
